import { forwardRef, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AttachmentPreviewSheet } from "@/components/job-reports-list";
import { SendForSignature } from "@/pages/close-out/wo-waiting";
import {
    api,
    ApiError,
    apiUrl,
    attachmentDownloadUrl,
    attachmentsZipUrl,
    downloadAttachmentFile,
    downloadUrlAsFile,
    getToken,
} from "@/lib/api";
import { useWoRegenerate } from "@/lib/use-wo-regenerate";
import { fileRowActions } from "./jobActions";
import { RowContextMenu, RowMoreMenu } from "./calm/row-tools";
import { groupDuplicates, isImageFile } from "./paperwork-rows";
import { buildDocumentFilename, sanitizeFilename, setDragOutData } from "@/lib/drag-out";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { cn, statusColor, formatDate, formatDateTime } from "@/lib/utils";
import { isOtherWaySend, type SendHistory } from "@/lib/use-send-history";
import { SentAnotherWayDialog, otherWayHow } from "@/components/sent-another-way-dialog";
import { ReportLinkStrip } from "@/components/report-link-strip";
import {
    Download,
    FileEdit,
    FileText,
    Link2,
    Loader2,
    MoreHorizontal,
    Receipt,
    RefreshCw,
    RotateCcw,
    Send,
    Trash2,
    Upload,
} from "lucide-react";
import { EmptyRow } from "./empty-row";
import { isRenameableCategory, provenanceLine } from "./paperwork-provenance";
import { RenameFileDialog } from "./rename-file-dialog";
import { HeldPacketBanner, usePacketStatus, useUseAsWorkOrder } from "./use-as-work-order";
import { useMakeOfficial } from "@/components/report-device-picker";
import { DevicePaperwork, type DeviceIn, type TestIn } from "./device-paperwork";
import { CityFilingTrail } from "./city-filing-trail";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { LinkedEstimate, LinkedInvoice, ReportAttachment } from "./types";
import { orgTodayKey } from "@/lib/datetime";

type ArtifactKind =
    | "document"
    | "test_report"
    | "test_report_official"
    | "inspection_report"
    | "summary"
    | "work_order"
    /** Closeout's "Additional files" (category=closeout_upload). */
    | "closeout_upload"
    /** A paper completed WO in the Work Order slot (category=work_order_uploaded). */
    | "work_order_uploaded";

interface Artifact extends ReportAttachment {
    kind: ArtifactKind;
}

const KIND_LABEL: Record<ArtifactKind, string> = {
    document: "Document",
    test_report: "Test report",
    test_report_official: "Official test report",
    inspection_report: "Inspection report",
    summary: "Cheat sheet",
    work_order: "Completed WO",
    closeout_upload: "Closeout upload",
    work_order_uploaded: "Completed WO",
};

/**
 * The `job_attachments.category` each row actually carries. `kind` is derived
 * from WHICH list a row arrived in, so this mapping is exact — safer than
 * reading `a.category` off the payload, and it is what the rename and remove
 * rules key on (#1538).
 */
const STORAGE_CATEGORY: Record<ArtifactKind, string> = {
    document: "document",
    test_report: "test_report",
    test_report_official: "test_report_official",
    inspection_report: "report",
    summary: "summary",
    work_order: "work_order_filled",
    closeout_upload: "closeout_upload",
    work_order_uploaded: "work_order_uploaded",
};

/** Per-device report rows — removable from the job via the soft-archive that
 *  Closeout has always used (`archive-report`), now offered here too (#1538). */
const REPORT_KINDS: ArtifactKind[] = ["test_report", "test_report_official"];

// ── Filter chips ─────────────────────────────────────────────────────────
// "Work orders" = the completed (filled) WO plus the original vendor PDF
// from intake — recognized by its provenance note ("Original work order
// (intake)" / "(auto-intake)"), since it lands as a plain
// category='document' row at job creation.
// "Images" (audit 2026-10-01): photos are not documents — the tech's test
// photos already live in the Photos card. They leave every other chip and
// get their own, so nothing becomes unreachable.
type PaperworkFilter = "all" | "test" | "work_orders" | "other" | "images";

const TEST_KINDS: ArtifactKind[] = ["test_report", "test_report_official", "inspection_report", "summary"];

function isOriginalWoDocument(a: Artifact): boolean {
    return a.kind === "document" && (a.notes ?? "").startsWith("Original work order");
}

function isWorkOrderArtifact(a: Artifact): boolean {
    return a.kind === "work_order" || a.kind === "work_order_uploaded" || isOriginalWoDocument(a);
}

/** Rows a signed WO wrongly lands in — the ones "Use as completed work order"
 *  is offered on (threads/wo-in-wrong-slot). Never the vendor's original WO. */
function isMovableToWorkOrder(a: Artifact): boolean {
    return (a.kind === "document" || a.kind === "closeout_upload") && !isOriginalWoDocument(a);
}

/** Rows "Make official" is offered on (2026-10-06): a plain PDF on the job
 *  that may be the city's report — never the vendor's original WO. Also an
 *  official report with NO device (2026-10-15, #072659090): it was filed
 *  against the job itself, so the packet never counted it; Make official
 *  links it to its device. Whether the name ends in .pdf doesn't matter
 *  there — it is already stored as a PDF report. */
function isConvertibleToReport(a: Artifact): boolean {
    if (a.kind === "test_report_official") return !a.asset_id;
    return (
        (a.kind === "document" || a.kind === "closeout_upload") &&
        !isOriginalWoDocument(a) &&
        (a.file_name ?? "").toLowerCase().endsWith(".pdf")
    );
}

/** A loose PDF "Match all reports" would read: a plain document or Closeout
 *  upload that is a PDF and isn't the vendor's original WO. */
function isLoosePdf(d: ReportAttachment): boolean {
    return (d.file_name ?? "").toLowerCase().endsWith(".pdf") && !(d.notes ?? "").startsWith("Original work order");
}

function matchesFilter(a: Artifact, filter: PaperworkFilter): boolean {
    if (filter === "images") return isImageFile(a);
    if (isImageFile(a)) return false;
    switch (filter) {
        case "all":
            return true;
        case "test":
            return TEST_KINDS.includes(a.kind);
        case "work_orders":
            return isWorkOrderArtifact(a);
        case "other":
            return !TEST_KINDS.includes(a.kind) && !isWorkOrderArtifact(a);
    }
}

const FILTER_LABEL: Record<PaperworkFilter, string> = {
    all: "All",
    test: "Test documents",
    work_orders: "Work orders",
    other: "Other",
    images: "Images",
};

// ── Drag OUT (universal drag & drop item 3) ──────────────────────────────
// Every row can be dragged out of the browser into Gmail/Finder/desktop.
// Generated documents get the clean convention "<DocType> <JobNumber> —
// <Customer>.pdf"; office-uploaded documents keep their own (human) name.

function artifactDragName(a: Artifact, jobNumber: string | null, customerName: string | null): string {
    // A photo drags out under its device label ("SN-40613G Tag 2026-10-26.jpg").
    if (a.kind === "document" && a.download_name) return sanitizeFilename(a.download_name);
    if (a.kind === "document" && !isOriginalWoDocument(a)) return sanitizeFilename(a.file_name);
    const docType = isOriginalWoDocument(a) ? "Original WO" : KIND_LABEL[a.kind];
    return buildDocumentFilename(docType, jobNumber, customerName);
}

/** Live-rendered invoice/estimate PDFs — token-in-query URL for DownloadURL. */
function reportPdfDragUrl(kind: "invoice" | "estimate", id: string): string {
    const token = getToken();
    return apiUrl(`/api/reports/${kind}/${id}/pdf${token ? `?token=${token}` : ""}`);
}

/**
 * Paperwork card (spec §3.3): Documents + Test Reports + Inspection Reports +
 * linked invoices/estimates as one artifact list. Checkbox selection reveals
 * a batch bar (Download zip / admin Regenerate); Upload lives in the header;
 * invoice rows keep their Send action and draft/sent chip.
 */
/**
 * "Customer packet sent Fri Sep 12, 2:41 PM · 3 recipients" — and, when the
 * last send left someone out, the same line in red with the count (#1545).
 *
 * Renders nothing at all when the packet has never gone out. That is the rule
 * for the whole feature, not a display choice: the FIRST send flips the job,
 * issues the draft invoice and can charge a card on file, so it belongs in
 * close-out behind those gates. There is nothing here to offer until a packet
 * has actually been sent.
 */
function PacketStrip({ history, onResend }: { history: SendHistory | null; onResend?: () => void }) {
    if (!history?.everSent) return null;
    // A staff-only TEST copy is never the customer's packet (2026-10-07).
    const customerSends = history.sends.filter((s) => !s.internalCopy);
    if (customerSends.length === 0) {
        const test = history.sends[0];
        return (
            <div
                data-testid="packet-strip"
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-border bg-card px-3 py-2"
            >
                <div className="min-w-0 text-[12.5px]">
                    <span className="font-semibold">Only a test copy went out</span>{" "}
                    {test ? formatDateTime(test.sentAt) : ""} · staff only.{" "}
                    <span className="text-muted-foreground">The customer hasn&apos;t been sent anything.</span>
                </div>
                {onResend && (
                    <Button size="sm" onClick={onResend} className="h-7 shrink-0 text-xs">
                        <RotateCcw className="mr-1 h-3 w-3" /> Send packet
                    </Button>
                )}
            </div>
        );
    }
    const last = customerSends.find((s) => s.delivered > 0) ?? customerSends[0];
    const otherWay = last ? isOtherWaySend(last) : false;
    const missed = history.missedOnLastSend;
    const recipients = last ? last.delivered : 0;
    const docs = last
        ? [
              ...new Set(
                  last.recipients
                      .filter((r) => r.status === "sent" || r.status === "delivered")
                      .flatMap((r) => r.documents),
              ),
          ]
        : [];

    return (
        <div
            data-testid="packet-strip"
            className={cn(
                "flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2",
                missed > 0
                    ? "border-destructive/50 bg-destructive/5"
                    : "border-sky-200 bg-sky-50 dark:border-sky-500/30 dark:bg-sky-500/10",
            )}
        >
            <div className="min-w-0">
                <div className="text-[12.5px]">
                    {missed > 0 ? (
                        <>
                            <span className="font-semibold text-destructive">
                                Packet sent {last ? formatDateTime(last.sentAt) : ""} — but {missed}{" "}
                                {missed === 1 ? "person" : "people"} didn&apos;t get it
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="font-semibold">Customer packet sent</span>{" "}
                            {last ? formatDateTime(last.sentAt) : ""} ·{" "}
                            {otherWay && last ? (
                                <span className="font-semibold" data-testid="packet-strip-other-way">
                                    {otherWayHow(last.channel, last.channelLabel)} (no email)
                                </span>
                            ) : (
                                <span className="font-semibold">
                                    {recipients} recipient{recipients === 1 ? "" : "s"}
                                </span>
                            )}
                        </>
                    )}
                </div>
                <div className="mt-0.5 text-[11px] text-foreground/60">
                    {docs.length > 0 ? docs.join(" · ") : "No documents recorded on that send"}
                    {last?.sentByName ? ` — ${otherWay ? "marked" : "sent"} by ${last.sentByName}` : ""}
                    {otherWay && last?.note ? ` · “${last.note}”` : ""}
                    {history.sends.length > 1 ? ` · ${history.sends.length} sends, see Comms` : ""}
                </div>
            </div>
            {onResend && (
                <Button size="sm" onClick={onResend} className="h-7 shrink-0 text-xs">
                    <RotateCcw className="mr-1 h-3 w-3" /> Resend packet
                </Button>
            )}
        </div>
    );
}

export const PaperworkCard = forwardRef<
    HTMLDivElement,
    {
        jobId: string;
        /** For clean drag-out filenames: "<DocType> <JobNumber> — <Customer>.pdf". */
        jobNumber?: string | null;
        customerName?: string | null;
        tradeCode: string | null;
        documents: ReportAttachment[];
        testReports: ReportAttachment[];
        /** Purveyor-portal official report PDFs filed via the drop zone (category=test_report_official). */
        officialReports: ReportAttachment[];
        inspectionReports: ReportAttachment[];
        /** Online Reporting Cheat Sheet (category=summary) — the only generated doc for portal jurisdictions. */
        summaryReports: ReportAttachment[];
        /** Completed vendor WO PDFs (category=work_order_filled). */
        workOrders: ReportAttachment[];
        /** Closeout "Additional files" (category=closeout_upload). */
        closeoutUploads?: ReportAttachment[];
        /** Paper completed WOs in the Work Order slot (category=work_order_uploaded). */
        uploadedWorkOrders?: ReportAttachment[];
        /** The vendor's WO number — lets the held-packet line spot a signed copy by name. */
        woNumber?: string | null;
        /** True for vendor_work_order jobs — surfaces the WO regenerate action (Issue #335 follow-up). */
        isWorkOrderJob?: boolean;
        /** Job's customer id — powers the "Fix template" picker next to Regenerate. */
        customerId?: string | null;
        /**
         * The template currently assigned to this job's WO (job.workOrder.template_id).
         * Passed on Regenerate so the office's chosen template is honored verbatim —
         * the fill won't re-derive a (possibly wrong) look-alike by perceptual hash.
         */
        assignedTemplateId?: string | null;
        invoices: LinkedInvoice[];
        estimates: LinkedEstimate[];
        isUploading: boolean;
        onUpload: (files: FileList | null) => void;
        onDeleteDocument: (attachmentId: string) => void;
        deletePending: boolean;
        onSendInvoice: (invoiceId: string) => void;
        /** #1545 — what this customer has already been sent. Null while the read
         *  is in flight or the job is out of scope for it; the strip then shows
         *  nothing rather than guessing. */
        sendHistory?: SendHistory | null;
        /** Opens the page's resend dialog. Undefined = not offered (no office
         *  role, or the packet has never gone out). */
        onResendPacket?: () => void;
        /**
         * Job page v2 (slice 3): every row gets the full row menu from the
         * job-actions list on ⋯ and on right-click (Preview, Make official,
         * Rename, Download, Use as completed WO, Remove report, Delete
         * document). Off (the classic page): unchanged.
         */
        rowMenus?: boolean;
        /** "Match all reports" (2026-10-09): opens the page's one-look matcher.
         *  Shown when loose PDFs (documents / Closeout uploads) sit on the job. */
        onMatchReports?: () => void;
        /** Paperwork by device (2026-10-09): one ✓/✗ tile per device. Omitted → no strip. */
        devicePaperwork?: { devices: DeviceIn[]; tests: TestIn[] };
    }
>(function PaperworkCard(
    {
        jobId,
        jobNumber = null,
        customerName = null,
        tradeCode,
        documents,
        testReports,
        officialReports,
        inspectionReports,
        summaryReports,
        workOrders,
        closeoutUploads = [],
        uploadedWorkOrders = [],
        woNumber = null,
        isWorkOrderJob = false,
        customerId = null,
        assignedTemplateId = null,
        invoices,
        estimates,
        isUploading,
        onUpload,
        onDeleteDocument,
        deletePending,
        onSendInvoice,
        sendHistory = null,
        onResendPacket,
        rowMenus = false,
        onMatchReports,
        devicePaperwork,
    },
    ref,
) {
    const { isAdmin, can } = useAuth();
    const qc = useQueryClient();
    // The held customer packet — and what it is ACTUALLY waiting on (reports,
    // the completed WO, or both). It used to read "Awaiting official report"
    // on every held job, including those whose reports were all in.
    const packetQ = usePacketStatus(jobId, can(PERMISSIONS.officeReview.view));
    const useAsWo = useUseAsWorkOrder({ jobId, packet: packetQ.data, invoices });
    const makeOfficial = useMakeOfficial(jobId);
    // Right-click a file → its menu (the same one the ⋯ button opens).
    const [menuFor, setMenuFor] = useState<string | null>(null);
    const canManagePaper = can(PERMISSIONS.officeReview.manage);
    const waitingOnReports = !!packetQ.data?.waiting_on_reports;
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [preview, setPreview] = useState<ReportAttachment | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [filter, setFilter] = useState<PaperworkFilter>("all");
    const [renaming, setRenaming] = useState<Artifact | null>(null);
    // "Already sent another way" (2026-10-07) on the held-packet line.
    const [otherWayOpen, setOtherWayOpen] = useState(false);

    const artifacts: Artifact[] = [
        ...documents.map((d) => ({ ...d, kind: "document" as const })),
        ...testReports.map((d) => ({ ...d, kind: "test_report" as const })),
        ...officialReports.map((d) => ({ ...d, kind: "test_report_official" as const })),
        ...inspectionReports.map((d) => ({ ...d, kind: "inspection_report" as const })),
        ...summaryReports.map((d) => ({ ...d, kind: "summary" as const })),
        ...workOrders.map((d) => ({ ...d, kind: "work_order" as const })),
        ...closeoutUploads.map((d) => ({ ...d, kind: "closeout_upload" as const })),
        ...uploadedWorkOrders.map((d) => ({ ...d, kind: "work_order_uploaded" as const })),
    ].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

    const visibleArtifacts = artifacts.filter((a) => matchesFilter(a, filter));
    // Exact duplicates (same name, uploader, minute, size) fold into one row
    // with a "+N copies" link; expanding lists the copies under it.
    const [openCopies, setOpenCopies] = useState<Set<string>>(new Set());
    const visibleGroups = groupDuplicates(visibleArtifacts);
    const displayRows = visibleGroups.flatMap((g) => [
        { a: g.primary, copyCount: g.copies.length, isCopy: false },
        ...(openCopies.has(g.primary.id) ? g.copies.map((c) => ({ a: c, copyCount: 0, isCopy: true })) : []),
    ]);
    const toggleCopies = (id: string) =>
        setOpenCopies((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    // ── Rename / relabel (#1538) ────────────────────────────────────────────
    // #819 B8 put a LABEL on the row — what people read in a list, with the
    // filename left alone. That was never enough: the filename is what
    // downloads, what rides the customer email, and what the city receives, and
    // on job 092678249 it was flat wrong on a file Corbin could not correct.
    // The ✎ now edits both, filename first.
    const renameFile = useMutation({
        mutationFn: ({ id, ...body }: { id: string; fileName?: string; photoTag?: string | null }) =>
            api.patch(`/api/attachments/${id}`, body),
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            setRenaming(null);
            toast.success(vars.fileName ? "File renamed — the job log has both names." : "Label saved.");
        },
        onError: (err) => {
            // The server is the gate; surface exactly what it refused rather
            // than a house-style "something went wrong".
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't rename the file.");
        },
    });

    const regenerateKind = tradeCode === "fire_ext" ? "fire-safety" : tradeCode === "backflow" ? "backflow" : null;
    // Regenerate rebuilds this job's report PDFs from current data — offer it
    // when the selection touches a generated report (same behavior the old
    // JobReportsList header action had).
    const selectionHasReport = artifacts.some((a) => a.kind !== "document" && selected.has(a.id));

    const toggle = (id: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const downloadSelected = async () => {
        const ids = [...selected].filter((id) => artifacts.some((a) => a.id === id));
        if (ids.length === 0) return;
        setDownloading(true);
        try {
            if (ids.length === 1) {
                const a = artifacts.find((x) => x.id === ids[0]);
                await downloadAttachmentFile(ids[0], a?.file_name);
            } else {
                await downloadUrlAsFile(attachmentsZipUrl(ids), `paperwork-${orgTodayKey()}.zip`);
            }
        } catch {
            toast.error("Download failed");
        } finally {
            setDownloading(false);
        }
    };

    // ── Remove a per-device report from the job (#1538) ───────────────────
    // The endpoint is the one Closeout has always called; it just was not
    // reachable from the job page, which is where the office is standing when
    // it notices a stray or blank copy. Soft-archive: off the job, out of the
    // customer packet, out of the city filing, still on file and restorable.
    // A report is evidence — the one place in Paperwork where a mistake must
    // not be final.
    const removeReport = useMutation({
        mutationFn: (attachmentId: string) => api.post(`/api/attachments/${attachmentId}/archive-report`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["job", jobId, "test-reports"] });
            qc.invalidateQueries({ queryKey: ["job", jobId, "official-test-reports"] });
            setSelected(new Set());
            toast.success("Report removed from the job — kept on file.");
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't remove the report.");
        },
    });

    /** The device's only report — pulling it leaves nothing to send or file. */
    const isLastReportOnJob = (a: Artifact) =>
        artifacts.filter((x) => REPORT_KINDS.includes(x.kind)).length <= 1 && REPORT_KINDS.includes(a.kind);

    const confirmRemoveReport = async (a: Artifact) => {
        const last = isLastReportOnJob(a);
        const detail = provenanceLine({
            category: STORAGE_CATEGORY[a.kind],
            uploaded_by_name: a.uploaded_by_name,
            created_at: a.created_at,
            file_size: a.file_size,
        });
        const ok = await confirmDialog({
            title: "Remove this file from the job?",
            message: last
                ? `${a.file_name} — ${detail}.\n\nThis is the only test report on the job. Remove it and there is nothing to send the customer and nothing to file with the city. Upload the replacement first if you have one.`
                : `${a.file_name} — ${detail}.\n\nIt comes off the job, out of the customer email, and out of the city filing. Nothing is deleted from storage — it stays on file, and the removal is recorded in the job log under your name.`,
            confirmLabel: last ? "Remove anyway" : "Remove",
            destructive: true,
            focusCancel: true,
        });
        if (ok) removeReport.mutate(a.id);
    };

    /** Job page v2: one row's whole menu, from the job-actions list. */
    const rowActionsFor = (a: Artifact) =>
        fileRowActions(
            {
                canMakeOfficial: canManagePaper && isConvertibleToReport(a),
                canRename: isRenameableCategory(STORAGE_CATEGORY[a.kind]),
                canUseAsWorkOrder: canManagePaper && !!isWorkOrderJob && isMovableToWorkOrder(a),
                isReport: REPORT_KINDS.includes(a.kind),
                isDocument: a.kind === "document",
            },
            {
                "preview-file": () => setPreview(a),
                "make-official": () => makeOfficial.start(a),
                "rename-file": () => setRenaming(a),
                "download-file": () =>
                    void downloadAttachmentFile(a.id, a.file_name).catch(() => toast.error("Download failed")),
                "use-as-completed-wo": () => void useAsWo.start(a),
                "remove-report": () => void confirmRemoveReport(a),
                "delete-document": () =>
                    void confirmDialog({
                        message: "Delete this document?",
                        destructive: true,
                        confirmLabel: "Delete",
                    }).then((ok) => ok && onDeleteDocument(a.id)),
            },
        );

    // ── Remove a completed work order from the job (#1445) ────────────────
    // Soft-archive, batched on purpose: the retry-loop wreckage (#1421) is
    // eighteen copies of one WO, and clearing it should cost one confirm, not
    // eighteen. The server keeps a job's newest completed WO when the request
    // would leave it with none and says so in `kept`, so the toast reports
    // what actually happened rather than what was asked for.
    const selectedWorkOrderIds = artifacts
        .filter((a) => a.kind === "work_order" && selected.has(a.id))
        .map((a) => a.id);
    /** A job's last completed WO is not removable — the vendor packet needs it. */
    const onlyWorkOrderLeft = workOrders.length <= 1;

    const afterWorkOrderChange = () => {
        qc.invalidateQueries({ queryKey: ["job", jobId, "work-orders-filled"] });
        setSelected(new Set());
    };

    const restoreWorkOrders = useMutation({
        mutationFn: (ids: string[]) =>
            api.post<{ restored: string[] }>("/api/attachments/restore-filled-work-orders", { attachmentIds: ids }),
        onSuccess: (res) => {
            afterWorkOrderChange();
            toast.success(
                res.restored.length === 1 ? "Put back on the job." : `${res.restored.length} put back on the job.`,
            );
        },
        onError: () => toast.error("Couldn't put those back."),
    });

    const removeWorkOrders = useMutation({
        mutationFn: (ids: string[]) =>
            api.post<{ removed: string[]; kept: string[]; skipped: string[] }>(
                "/api/attachments/archive-filled-work-orders",
                { attachmentIds: ids },
            ),
        onSuccess: (res, ids) => {
            afterWorkOrderChange();
            const removed = res.removed.length;
            if (removed === 0) {
                toast.error(
                    res.kept.length > 0
                        ? "Nothing removed — a job keeps its only completed work order."
                        : "Nothing was removed.",
                );
                return;
            }
            const eligible = ids.length - res.kept.length;
            const lines = [
                removed === 1 ? "Completed work order removed." : `${removed} copies removed from this job.`,
            ];
            if (removed < eligible) lines.push(`${removed} of ${eligible} — the rest were already gone.`);
            if (res.kept.length > 0) lines.push("The newest was kept.");
            toast.success(lines.join(" "), {
                action: { label: "Undo", onClick: () => restoreWorkOrders.mutate(res.removed) },
            });
        },
        onError: (err) => toast.error(err instanceof ApiError ? `Remove failed (${err.status})` : "Remove failed"),
    });

    const confirmRemoveWorkOrders = async (ids: string[]) => {
        const all = ids.length >= workOrders.length;
        const message =
            ids.length === 1
                ? "It comes off the job and out of the customer email. Nothing is deleted from storage — you can undo this."
                : `They come off the job and out of the customer email.${
                      all ? " The newest copy stays." : ""
                  } Nothing is deleted from storage — you can undo this.`;
        if (
            await confirmDialog({
                message,
                title: ids.length === 1 ? "Remove this completed work order?" : `Remove ${ids.length} copies?`,
                destructive: true,
                confirmLabel: ids.length === 1 ? "Remove" : `Remove ${ids.length}`,
            })
        )
            removeWorkOrders.mutate(ids);
    };

    const regenerate = useMutation({
        mutationFn: () => api.post(`/api/${regenerateKind}/job/${jobId}/regenerate-report`, {}),
        onSuccess: () => {
            toast.success("Report regenerated from current job data");
            qc.invalidateQueries({ queryKey: ["job", jobId, "test-reports"] });
            qc.invalidateQueries({ queryKey: ["job", jobId, "inspection-reports"] });
            qc.invalidateQueries({ queryKey: ["job", jobId, "summary-reports"] });
        },
        onError: (err) =>
            toast.error(err instanceof ApiError ? `Regenerate failed (${err.status})` : "Regenerate failed"),
    });

    // Regenerate the vendor return PDF from the tech's stored submission —
    // the office fix-loop for a wrong template/base document ("fix the
    // link, hit Regenerate"). Rides the existing office-review refill
    // endpoint: archives the superseded PDF, never auto-delivers. Lives
    // HERE (the job page) because this is where the office looks for it.
    const canRegenerateWo = isWorkOrderJob && can(PERMISSIONS.officeReview.manage);
    const regenerateWo = useWoRegenerate({
        jobId,
        onLandedAfterTimeout: () => qc.invalidateQueries({ queryKey: ["job", jobId, "work-orders-filled"] }),
        onSuccess: (data) => {
            const warnings = data?.fillWarnings ?? [];
            // A refill with no active template still answers 200 — say that no
            // PDF came out instead of claiming one did (Issue #931).
            if (data?.generated === false) {
                toast.error("No PDF was generated — nothing was re-filled.", {
                    description: warnings[0] ?? "This job has no locked return-PDF template to fill onto yet.",
                });
            } else if (warnings.length > 0) {
                toast.warning(`Work order regenerated with ${warnings.length} warning(s) — review before sending.`, {
                    description: warnings[0],
                });
            } else {
                toast.success("Work order PDF regenerated from the tech's submission — review before sending.");
            }
            qc.invalidateQueries({ queryKey: ["job", jobId, "work-orders-filled"] });
        },
    });

    // "Fix template" picker (Issue #335 follow-up): when the WO filled on the
    // wrong template variant, the office picks the right taught template HERE
    // — no hidden screens — and the PDF regenerates immediately.
    const woTemplatesQ = useQuery({
        queryKey: ["customer", customerId, "wo-templates"],
        queryFn: () =>
            api.get<{
                templates: Array<{ id: string; name: string; status: string; pdf_page_count: number | null }>;
            }>(`/api/customers/${customerId}/work-order-templates`),
        enabled: canRegenerateWo && !!customerId,
        staleTime: 60_000,
    });
    // Only locked forms can fill, so offering a draft here just produced a
    // switch that regenerated nothing (Issue #931).
    const woTemplateOptions = (woTemplatesQ.data?.templates ?? []).filter((t) => t.status === "active");
    const setWoTemplate = useMutation({
        mutationFn: (templateId: string) =>
            api.post(`/api/office-review/jobs/${jobId}/set-wo-template`, { templateId }),
        onSuccess: (_data, templateId) => {
            toast.success("Template switched — regenerating the PDF…");
            // Pass the just-chosen template so the fill pins to it instead of
            // re-deriving a look-alike by perceptual hash (Issue #614).
            void regenerateWo.start({ body: { templateId } });
        },
        onError: (err) =>
            toast.error(
                err instanceof ApiError && err.status === 409
                    ? "This job has no WO intake link — change the template's customer assignment instead."
                    : "Could not switch the template.",
            ),
    });

    const empty = artifacts.length === 0 && invoices.length === 0 && estimates.length === 0;
    // Loose PDFs that could still be a device's report.
    const loosePdfs = [...documents, ...closeoutUploads].filter((d) => isLoosePdf(d)).length;

    return (
        <Card ref={ref} id="paperwork-card">
            {makeOfficial.element}
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4" /> Paperwork
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {canRegenerateWo && woTemplateOptions.length > 0 && (
                            <select
                                className="h-9 rounded-md border border-border bg-card px-2 text-xs text-foreground"
                                value=""
                                disabled={setWoTemplate.isPending || regenerateWo.isRunning}
                                onChange={(e) => {
                                    if (e.target.value) setWoTemplate.mutate(e.target.value);
                                }}
                                title="Filled on the wrong form? Pick the taught template that matches this job's scanned WO — the PDF regenerates on the right one."
                            >
                                <option value="">Fix template…</option>
                                {woTemplateOptions.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}
                                        {t.pdf_page_count ? ` (${t.pdf_page_count} pages)` : ""}
                                    </option>
                                ))}
                            </select>
                        )}
                        {canRegenerateWo && (
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={regenerateWo.isRunning}
                                onClick={() =>
                                    void regenerateWo.start(
                                        assignedTemplateId ? { body: { templateId: assignedTemplateId } } : {},
                                    )
                                }
                                title="Re-render the vendor return PDF from the tech's stored submission (after fixing a wrong template or linked document). The old PDF is archived; nothing auto-sends."
                            >
                                {regenerateWo.isRunning ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                )}
                                Regenerate WO PDF
                            </Button>
                        )}
                        {/* 2026-11-03 (job 092678414): get the store manager's signature
                            from the job page too, not only from Work orders waiting. */}
                        {canRegenerateWo && jobId && <SendForSignature jobId={jobId} color="#004AAD" />}
                        {onMatchReports && canManagePaper && loosePdfs > 0 && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onMatchReports}
                                title="Read every loose PDF on this job and file each one to its device"
                                data-testid="match-all-reports"
                            >
                                <Link2 className="h-4 w-4 mr-1" />
                                Match {loosePdfs} report{loosePdfs === 1 ? "" : "s"}
                            </Button>
                        )}
                        <input
                            ref={fileRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => onUpload(e.target.files)}
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={isUploading}
                            onClick={() => fileRef.current?.click()}
                        >
                            {isUploading ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4 mr-1" />
                            )}
                            Upload
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* #1545 — the customer packet, above the documents it sends.
                    The packet is a JOB-level thing (report + photos + invoice +
                    estimate, consolidated into one email per recipient), so it
                    can't hang off any single document row. The per-invoice
                    "Send again" further down is a different, narrower action and
                    is deliberately untouched. */}
                {devicePaperwork && (
                    <DevicePaperwork jobId={jobId} devices={devicePaperwork.devices} tests={devicePaperwork.tests} />
                )}
                {devicePaperwork && (
                    <CityFilingTrail
                        jobId={jobId}
                        serials={
                            new Map(
                                devicePaperwork.devices
                                    .filter((d) => d.serial)
                                    .map((d) => [d.assetId, d.serial as string] as [string, string]),
                            )
                        }
                    />
                )}
                <PacketStrip history={sendHistory} onResend={onResendPacket} />
                <ReportLinkStrip jobId={jobId} canManage={canManagePaper} />
                <HeldPacketBanner
                    packet={packetQ.data}
                    candidates={isWorkOrderJob ? artifacts.filter(isMovableToWorkOrder) : []}
                    woNumber={woNumber}
                    onUse={(f) => void useAsWo.start(f)}
                    pending={useAsWo.pending}
                    onSentAnotherWay={canManagePaper ? () => setOtherWayOpen(true) : undefined}
                />
                <SentAnotherWayDialog jobId={jobId} open={otherWayOpen} onClose={() => setOtherWayOpen(false)} />
                {empty ? (
                    <EmptyRow label="No paperwork yet" actionLabel="Upload" onAction={() => fileRef.current?.click()} />
                ) : (
                    <>
                        {selected.size > 0 && (
                            <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-1.5">
                                <span className="text-xs text-foreground/70">{selected.size} selected</span>
                                <div className="flex items-center gap-2">
                                    {isAdmin && regenerateKind && selectionHasReport && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 px-2 text-xs"
                                            disabled={regenerate.isPending}
                                            onClick={() => regenerate.mutate()}
                                            title="Admin: rebuild the report PDFs from the current job data"
                                        >
                                            {regenerate.isPending ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                            ) : (
                                                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                                            )}
                                            Regenerate
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 px-2 text-xs"
                                        disabled={downloading}
                                        onClick={downloadSelected}
                                    >
                                        {downloading ? (
                                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                        ) : (
                                            <Download className="h-3.5 w-3.5 mr-1" />
                                        )}
                                        Download{selected.size > 1 ? ` (${selected.size}) as Zip` : ""}
                                    </Button>
                                    {selectedWorkOrderIds.length > 0 && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                                            disabled={removeWorkOrders.isPending || onlyWorkOrderLeft}
                                            onClick={() => void confirmRemoveWorkOrders(selectedWorkOrderIds)}
                                            title={
                                                onlyWorkOrderLeft
                                                    ? "This is the only completed work order on the job."
                                                    : "Remove the selected completed work orders — kept on file, undoable"
                                            }
                                        >
                                            {removeWorkOrders.isPending ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                            ) : (
                                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                            )}
                                            Remove ({selectedWorkOrderIds.length})
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                        {artifacts.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {(Object.keys(FILTER_LABEL) as PaperworkFilter[]).map((f) => {
                                    // Rows the chip shows, duplicates folded — the
                                    // number matches what you see.
                                    const count = groupDuplicates(artifacts.filter((a) => matchesFilter(a, f))).length;
                                    if (f === "images" && count === 0) return null;
                                    return (
                                        <button
                                            key={f}
                                            type="button"
                                            onClick={() => setFilter(f)}
                                            className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                                                filter === f
                                                    ? "border-primary bg-primary/10 font-medium text-primary"
                                                    : "text-foreground/70 hover:bg-muted/50"
                                            }`}
                                        >
                                            {FILTER_LABEL[f]} ({count})
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {artifacts.length > 0 && visibleArtifacts.length === 0 && (
                            <p className="px-1 text-xs text-foreground/60">
                                {filter === "all"
                                    ? "No documents on this job — only images (see Images)."
                                    : `No ${FILTER_LABEL[filter].toLowerCase()} on this job.`}
                            </p>
                        )}
                        {visibleArtifacts.length > 0 && (
                            <div className="rounded-lg border divide-y text-sm">
                                {displayRows.map(({ a, copyCount, isCopy }) => {
                                    const rowEl = (
                                        <div
                                            key={a.id}
                                            className={cn(
                                                "flex items-center gap-2 px-3 py-2 hover:bg-muted/20 transition-colors cursor-grab active:cursor-grabbing",
                                                isCopy && "pl-8",
                                            )}
                                            data-testid={isCopy ? "paperwork-copy-row" : "paperwork-row"}
                                            onContextMenu={(e) => {
                                                // Job page v2: the row's RowContextMenu owns right-click.
                                                if (rowMenus) return;
                                                const hasMenu =
                                                    canManagePaper &&
                                                    (isConvertibleToReport(a) ||
                                                        (isWorkOrderJob && isMovableToWorkOrder(a)));
                                                if (!hasMenu) return;
                                                e.preventDefault();
                                                setMenuFor(a.id);
                                            }}
                                            draggable
                                            onDragStart={(e) =>
                                                setDragOutData(e, {
                                                    url: attachmentDownloadUrl(a.id, { disposition: "attachment" }),
                                                    filename: artifactDragName(a, jobNumber, customerName),
                                                })
                                            }
                                            title="Drag out to Gmail, Finder, or your desktop"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected.has(a.id)}
                                                onChange={() => toggle(a.id)}
                                                className="h-3.5 w-3.5 shrink-0"
                                                aria-label={`Select ${a.file_name}`}
                                            />
                                            <FileText className="h-4 w-4 text-foreground/70 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <button
                                                    type="button"
                                                    className="block w-full min-w-0 truncate text-left hover:underline"
                                                    onClick={() => setPreview(a)}
                                                    title={
                                                        a.photo_label
                                                            ? a.photo_label
                                                            : a.photo_tag
                                                              ? `${a.photo_tag} — ${a.file_name}`
                                                              : a.file_name
                                                    }
                                                >
                                                    {a.photo_label || a.photo_tag || a.file_name}
                                                    {!a.photo_label && a.photo_tag && (
                                                        <span className="ml-1.5 text-xs text-muted-foreground">
                                                            {a.file_name}
                                                        </span>
                                                    )}
                                                </button>
                                                {/* #1538 — where it came from, when, and how big. Two rows
                                                carrying the same name are told apart here, before any
                                                rename or remove button is safe to press. */}
                                                <span className="block truncate text-[11px] text-foreground/60">
                                                    {/* Leads the line so truncation never eats it. */}
                                                    {copyCount > 0 && (
                                                        <button
                                                            type="button"
                                                            className="mr-1.5 font-medium text-primary hover:underline"
                                                            onClick={() => toggleCopies(a.id)}
                                                            aria-expanded={openCopies.has(a.id)}
                                                        >
                                                            {openCopies.has(a.id)
                                                                ? "Hide copies"
                                                                : `+${copyCount} ${copyCount === 1 ? "copy" : "copies"}`}
                                                        </button>
                                                    )}
                                                    {isCopy ? "Copy · " : ""}
                                                    {provenanceLine({
                                                        category: STORAGE_CATEGORY[a.kind],
                                                        uploaded_by_name: a.uploaded_by_name,
                                                        created_at: a.created_at,
                                                        file_size: a.file_size,
                                                    })}
                                                </span>
                                            </div>
                                            {isRenameableCategory(STORAGE_CATEGORY[a.kind]) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0 shrink-0"
                                                    disabled={renameFile.isPending}
                                                    onClick={() => setRenaming(a)}
                                                    aria-label={`Rename ${a.file_name}`}
                                                    title="Rename this file — changes the real filename"
                                                >
                                                    <FileEdit className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                            <Badge
                                                variant="outline"
                                                className="text-[10px] shrink-0 text-foreground/70"
                                            >
                                                {isOriginalWoDocument(a) ? "Original WO" : KIND_LABEL[a.kind]}
                                            </Badge>
                                            <span className="text-xs text-foreground/60 shrink-0">
                                                {formatDate(a.created_at)}
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0 shrink-0"
                                                onClick={() =>
                                                    downloadAttachmentFile(a.id, a.file_name).catch(() =>
                                                        toast.error("Download failed"),
                                                    )
                                                }
                                                aria-label="Download"
                                            >
                                                <Download className="h-4 w-4" />
                                            </Button>
                                            {canManagePaper && isConvertibleToReport(a) && waitingOnReports && (
                                                <Button
                                                    size="sm"
                                                    className="h-7 shrink-0 px-2.5 text-xs"
                                                    disabled={makeOfficial.pending}
                                                    onClick={() => makeOfficial.start(a)}
                                                    title="Turn this file into the official city report for a device on this job"
                                                >
                                                    Make official
                                                </Button>
                                            )}
                                            {rowMenus && (
                                                <RowMoreMenu
                                                    actions={rowActionsFor(a)}
                                                    label={`More for ${a.file_name}`}
                                                    testId="file-row-more"
                                                />
                                            )}
                                            {!rowMenus &&
                                                canManagePaper &&
                                                (isConvertibleToReport(a) ||
                                                    (isWorkOrderJob && isMovableToWorkOrder(a))) && (
                                                    <DropdownMenu
                                                        open={menuFor === a.id}
                                                        onOpenChange={(o) => setMenuFor(o ? a.id : null)}
                                                    >
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 w-7 p-0 shrink-0"
                                                                aria-label={`More for ${a.file_name}`}
                                                            >
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-64">
                                                            {isConvertibleToReport(a) && (
                                                                <DropdownMenuItem
                                                                    disabled={makeOfficial.pending}
                                                                    onSelect={() => makeOfficial.start(a)}
                                                                >
                                                                    <div>
                                                                        <div className="font-semibold text-primary">
                                                                            Make it the official report
                                                                        </div>
                                                                        <div className="text-[11px] text-muted-foreground">
                                                                            Files it to a device on this job
                                                                        </div>
                                                                    </div>
                                                                </DropdownMenuItem>
                                                            )}
                                                            {isWorkOrderJob && isMovableToWorkOrder(a) && (
                                                                <DropdownMenuItem
                                                                    disabled={useAsWo.pending}
                                                                    onSelect={() => void useAsWo.start(a)}
                                                                >
                                                                    <div>
                                                                        <div className="font-semibold">
                                                                            Use as completed work order
                                                                        </div>
                                                                        <div className="text-[11px] text-muted-foreground">
                                                                            Moves it into the Work Order slot
                                                                        </div>
                                                                    </div>
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            {a.kind === "document" && (
                                                <button
                                                    type="button"
                                                    className="text-destructive hover:opacity-80 shrink-0 disabled:opacity-50"
                                                    disabled={deletePending}
                                                    onClick={async () => {
                                                        if (
                                                            await confirmDialog({
                                                                message: "Delete this document?",
                                                                destructive: true,
                                                                confirmLabel: "Delete",
                                                            })
                                                        )
                                                            onDeleteDocument(a.id);
                                                    }}
                                                    aria-label="Delete document"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {REPORT_KINDS.includes(a.kind) && (
                                                <button
                                                    type="button"
                                                    className="text-destructive hover:opacity-80 shrink-0 disabled:opacity-50"
                                                    disabled={removeReport.isPending}
                                                    onClick={() => void confirmRemoveReport(a)}
                                                    aria-label={`Remove ${a.file_name} from the job`}
                                                    title="Remove from the job — kept on file, undoable"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {a.kind === "work_order" && (
                                                <button
                                                    type="button"
                                                    className="text-destructive hover:opacity-80 shrink-0 disabled:opacity-50"
                                                    disabled={removeWorkOrders.isPending || onlyWorkOrderLeft}
                                                    onClick={() => void confirmRemoveWorkOrders([a.id])}
                                                    aria-label="Remove completed work order"
                                                    title={
                                                        onlyWorkOrderLeft
                                                            ? "This is the only completed work order on the job."
                                                            : "Remove from the job — kept on file, undoable"
                                                    }
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                    return rowMenus ? (
                                        <RowContextMenu
                                            key={a.id}
                                            actions={rowActionsFor(a)}
                                            testId="file-context-menu"
                                        >
                                            {rowEl}
                                        </RowContextMenu>
                                    ) : (
                                        rowEl
                                    );
                                })}
                            </div>
                        )}
                        {(invoices.length > 0 || estimates.length > 0) && (
                            <div className="space-y-2">
                                {invoices.map((inv) => {
                                    // #1142: the status enum has no 'sent' state
                                    // — emailing an invoice leaves it 'open' —
                                    // so status alone said "Not yet sent" forever
                                    // on an invoice the office had already sent.
                                    // The send ledger is the truth.
                                    const emailedCount = inv.emailed_count ?? 0;
                                    const neverSent = emailedCount === 0;
                                    // A voided or fully paid invoice needs no
                                    // send prompt regardless of its history.
                                    const stillSendable = inv.status === "draft" || inv.status === "open";
                                    const mayBeUnsent = stillSendable && neverSent;
                                    return (
                                        <div
                                            key={inv.id}
                                            className="rounded-lg border p-3 hover:bg-muted/30 transition-colors"
                                            draggable
                                            onDragStart={(e) =>
                                                setDragOutData(e, {
                                                    url: reportPdfDragUrl("invoice", inv.id),
                                                    filename: buildDocumentFilename(
                                                        "Invoice",
                                                        inv.invoice_number || jobNumber,
                                                        customerName,
                                                    ),
                                                })
                                            }
                                            title="Drag out to Gmail, Finder, or your desktop"
                                        >
                                            <Link
                                                to={`/commercial/${inv.id}?type=invoice`}
                                                className="flex items-center justify-between"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Receipt className="h-4 w-4 text-foreground/80 shrink-0" />
                                                    <span className="font-mono text-sm font-semibold shrink-0">
                                                        Invoice #{inv.invoice_number || inv.id.slice(0, 8)}
                                                    </span>
                                                    {inv.title && (
                                                        <span className="text-sm text-foreground/70 truncate">
                                                            — {inv.title}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {inv.total_amount != null && (
                                                        <span className="text-sm font-medium">
                                                            ${Number(inv.total_amount).toFixed(2)}
                                                        </span>
                                                    )}
                                                    <Badge
                                                        variant="outline"
                                                        className={`text-[10px] ${statusColor(inv.status)}`}
                                                    >
                                                        {inv.status}
                                                    </Badge>
                                                </div>
                                            </Link>
                                            {mayBeUnsent && (
                                                <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2">
                                                    <span className="text-[11px] text-amber-700 dark:text-amber-300">
                                                        Not yet sent to the customer.
                                                    </span>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 text-xs"
                                                        onClick={() => onSendInvoice(inv.id)}
                                                    >
                                                        <Send className="h-3 w-3 mr-1" /> Send invoice
                                                    </Button>
                                                </div>
                                            )}
                                            {stillSendable && !neverSent && (
                                                <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2">
                                                    {(inv.undelivered_count ?? 0) >= emailedCount ? (
                                                        // Every email of this invoice bounced or was
                                                        // blocked: "sent" would be the lie this fixes.
                                                        <span
                                                            className="text-[11px] font-medium text-destructive"
                                                            data-testid="invoice-undelivered"
                                                        >
                                                            Emailed, but it didn&apos;t arrive
                                                            {inv.undelivered_note ? ` — ${inv.undelivered_note}` : ""}
                                                        </span>
                                                    ) : (
                                                        <span className="text-[11px] text-foreground/60">
                                                            {emailedCount === 1
                                                                ? "Sent to the customer"
                                                                : `Sent ${emailedCount} times`}
                                                            {inv.last_emailed_at
                                                                ? ` · ${new Date(inv.last_emailed_at).toLocaleDateString()}`
                                                                : ""}
                                                            {(inv.undelivered_count ?? 0) > 0
                                                                ? ` · ${inv.undelivered_count} didn't arrive`
                                                                : ""}
                                                        </span>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 text-xs"
                                                        onClick={() => onSendInvoice(inv.id)}
                                                    >
                                                        <Send className="h-3 w-3 mr-1" /> Send again
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {estimates.map((est) => (
                                    <Link
                                        key={est.id}
                                        to={`/commercial/${est.id}?type=estimate`}
                                        className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/30 transition-colors"
                                        draggable
                                        onDragStart={(e) =>
                                            setDragOutData(e, {
                                                url: reportPdfDragUrl("estimate", est.id),
                                                filename: buildDocumentFilename(
                                                    "Estimate",
                                                    est.display_number || jobNumber,
                                                    customerName,
                                                ),
                                            })
                                        }
                                        title="Drag out to Gmail, Finder, or your desktop"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FileEdit className="h-4 w-4 text-foreground/80 shrink-0" />
                                            <span className="font-mono text-sm font-semibold shrink-0">
                                                Estimate #{est.display_number || est.id.slice(0, 8)}
                                            </span>
                                            {est.title && (
                                                <span className="text-sm text-foreground/70 truncate">
                                                    — {est.title}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {est.subtotal != null && (
                                                <span className="text-sm font-medium">
                                                    ${Number(est.subtotal).toFixed(2)}
                                                </span>
                                            )}
                                            <Badge
                                                variant="outline"
                                                className={`text-[10px] ${statusColor(est.status)}`}
                                            >
                                                {est.status}
                                            </Badge>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </CardContent>
            <AttachmentPreviewSheet attachment={preview} onClose={() => setPreview(null)} />
            <RenameFileDialog
                open={renaming !== null}
                onClose={() => setRenaming(null)}
                fileName={renaming?.file_name ?? ""}
                photoTag={renaming?.photo_tag ?? null}
                provenance={
                    renaming
                        ? provenanceLine({
                              category: STORAGE_CATEGORY[renaming.kind],
                              uploaded_by_name: renaming.uploaded_by_name,
                              created_at: renaming.created_at,
                              file_size: renaming.file_size,
                          })
                        : null
                }
                // An official's name comes from the device's recorded result, so
                // say plainly that renaming the file does not change the result.
                // Otherwise a rename looks like a fix, and the device keeps
                // reading INCOMPLETE on the Grid with its clock unmoved — which
                // is exactly how job 092678249 got here.
                derivedNameNote={
                    renaming?.kind === "test_report_official"
                        ? "Groundwork normally names this file from the device's recorded test result. Renaming it does not change that result, and the next official you upload for this device will be named off the record again. If the result itself is wrong, fix it on the device row first."
                        : null
                }
                saving={renameFile.isPending}
                onSave={(next) => renaming && renameFile.mutate({ id: renaming.id, ...next })}
            />
        </Card>
    );
});
